import axios from 'axios';
import { authService } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'https://salvages.definiteassurance.com/api';

// Regular JSON API client
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// File upload API client (multipart/form-data)
export const uploadApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  timeout: 60000, // 60 seconds for file uploads
});

// Request interceptor to add token to both clients
const addTokenInterceptor = (config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Error interceptor - only refresh token for expired tokens, not for login failures
const errorInterceptor = async (error) => {
  const originalRequest = error.config;
  
  // Check if this is a login request - don't refresh token for login failures
  if (originalRequest.url?.includes('/auth/login')) {
    return Promise.reject(error);
  }

  // Only attempt token refresh for 401 errors on non-login endpoints
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const newToken = await authService.refreshToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      
      // Retry with the appropriate client
      if (originalRequest.headers['Content-Type'] === 'multipart/form-data') {
        return uploadApi(originalRequest);
      }
      return api(originalRequest);
    } catch (refreshError) {
      // Redirect to login if refresh fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    }
  }

  return Promise.reject(error);
};

// Add interceptors to regular api
api.interceptors.request.use(addTokenInterceptor, (error) => Promise.reject(error));
api.interceptors.response.use((response) => response, errorInterceptor);

// Add interceptors to upload api
uploadApi.interceptors.request.use(addTokenInterceptor, (error) => Promise.reject(error));
uploadApi.interceptors.response.use((response) => response, errorInterceptor);

// Helper function to determine which client to use
export const getApiClient = (config = {}) => {
  const isMultipart = config.headers?.['Content-Type'] === 'multipart/form-data' || 
                      config.data instanceof FormData;
  
  return isMultipart ? uploadApi : api;
};

// Generic request function that automatically uses the right client
export const request = async (method, url, data = null, config = {}) => {
  const client = getApiClient({ ...config, data });
  
  switch (method.toLowerCase()) {
    case 'get':
      return client.get(url, config);
    case 'post':
      return client.post(url, data, config);
    case 'put':
      return client.put(url, data, config);
    case 'patch':
      return client.patch(url, data, config);
    case 'delete':
      return client.delete(url, config);
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
};

export default api;